/**
 * Outil « Puissances » (Boîte à outils, 5e → 2nde).
 *
 * Puissance : aⁿ = a × a × … (carré et cube dessinés) ; exposant négatif =
 * inverse (4e : seulement pour 10 ; dès la 3e pour tout nombre). Échelle :
 * de l'atome à la Voie lactée, chaque cran × 10, préfixes nano → téra.
 * Notation scientifique : la virgule se déplace, l'exposant compense.
 * Règles : produit (4e), quotient (3e), même exposant, en comptant les
 * facteurs. Calculs exacts : `powers.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, INK } from '../../ui/svg-parts';
import {
  exponentRange,
  objectsAt,
  powerSteps,
  powerValue,
  PREFIXES,
  powTex,
  ruleSteps,
  SCALE,
  SCALE_MAX,
  SCALE_MIN,
  scientific,
  scientificSteps,
  tenPowerDecimal,
  sup,
  tenTo,
  type Rule,
  type Step,
} from './powers';

type Mode = 'power' | 'scale' | 'sci' | 'rules';

const C1 = 'var(--color-primary-500)';
const C2 = 'var(--color-accent-500)';
const VB_W = 340;
const NBSP = String.fromCharCode(0xa0);
const fmtExp = (v: number) => (v < 0 ? `−${-v}` : String(v));

export function PowersTool({ onSend, sendCost, level }: ToolProps & { level?: string | null }) {
  const rank = Math.min(5, Math.max(2, schoolLevelRank(level) ?? 4));
  const [mode, setMode] = useState<Mode>('power');
  const options: Array<{ value: Mode; label: string }> =
    rank >= 3
      ? [
          { value: 'power', label: 'Puissance' },
          { value: 'scale', label: 'Échelle' },
          // notation scientifique : 3e (programme de cycle 4, BO du 5 mars 2026)
          ...(rank >= 4 ? [{ value: 'sci' as const, label: 'Écriture scientifique' }] : []),
          { value: 'rules', label: 'Règles' },
        ]
      : [{ value: 'power', label: 'Puissance' }];
  return (
    <div className="space-y-4">
      {options.length > 1 && (
        <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      )}
      <div hidden={mode !== 'power'} className="space-y-4">
        <PowerMode rank={rank} onSend={onSend} sendCost={sendCost} />
      </div>
      {rank >= 3 && (
        <>
          <div hidden={mode !== 'scale'} className="space-y-4">
            <ScaleMode />
          </div>
          {rank >= 4 && (
            <div hidden={mode !== 'sci'} className="space-y-4">
              <SciMode onSend={onSend} sendCost={sendCost} />
            </div>
          )}
          <div hidden={mode !== 'rules'} className="space-y-4">
            <RulesMode rank={rank} />
          </div>
        </>
      )}
    </div>
  );
}

function StepList({ steps, testId }: { steps: Step[]; testId?: string }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {steps.map((s, i) => (
        <div key={i} className="space-y-0.5">
          <MathText className="text-ink-900 block overflow-x-auto py-0.5 text-lg">{`$${s.tex}$`}</MathText>
          <p className="text-ink-600 text-sm">{frenchSpaces(s.say)}</p>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Puissance                                                           */
/* ------------------------------------------------------------------ */

function PowerMode({ rank, onSend, sendCost }: ToolProps & { rank: number }) {
  const [base, setBase] = useState(rank <= 2 ? 4 : 2);
  const [exp, setExp] = useState(3);
  const [lo, hi] = exponentRange(rank, base);
  const n = Math.min(hi, Math.max(lo, exp));
  const v = powerValue(base, n);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Stepper label="Nombre" value={base} min={1} max={12} onChange={setBase} />
        <Stepper label="Exposant" value={n} min={lo} max={hi} onChange={setExp} format={fmtExp} />
      </div>
      {rank === 3 && base !== 10 && (
        <p className="text-ink-500 text-xs">
          {frenchSpaces(
            'En 4e, les exposants sont positifs ; seul 10 a des exposants négatifs, pour les préfixes (milli, micro, nano) : choisis 10 pour en voir.',
          )}
        </p>
      )}
      {(n === 2 || n === 3) && <SquareCube base={base} n={n} />}
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3" aria-live="polite">
        <StepList steps={powerSteps(base, n)} testId="power-steps" />
        {onSend && (
          <SendToSheetButton
            label={v.plain}
            cost={sendCost}
            onClick={() => onSend({ atoms: v.atoms })}
          />
        )}
      </section>
    </>
  );
}

/** a² en carré quadrillé, a³ en cube en perspective */
function SquareCube({ base, n }: { base: number; n: 2 | 3 }) {
  const H = 200;
  if (n === 2) {
    const S = 160;
    const x0 = (VB_W - S) / 2;
    const y0 = 20;
    const c = S / base;
    return (
      <svg
        viewBox={`0 0 ${VB_W} ${H}`}
        className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Un carré de côté ${base} : ${base * base} petits carrés.`}
      >
        <rect
          x={x0}
          y={y0}
          width={S}
          height={S}
          fill={C1}
          fillOpacity={0.2}
          stroke={INK}
          strokeWidth={1.5}
        />
        {Array.from({ length: base - 1 }, (_, i) => (
          <g key={i}>
            <line
              x1={x0 + (i + 1) * c}
              x2={x0 + (i + 1) * c}
              y1={y0}
              y2={y0 + S}
              stroke={C1}
              strokeWidth={1}
            />
            <line
              x1={x0}
              x2={x0 + S}
              y1={y0 + (i + 1) * c}
              y2={y0 + (i + 1) * c}
              stroke={C1}
              strokeWidth={1}
            />
          </g>
        ))}
        <text x={x0 + S / 2} y={y0 + S + 16} textAnchor="middle" fontSize={13} fill={INK}>
          {base}
        </text>
        <text x={x0 - 8} y={y0 + S / 2 + 5} textAnchor="end" fontSize={13} fill={INK}>
          {base}
        </text>
      </svg>
    );
  }
  const S = 110;
  const dx = S * 0.5;
  const dy = S * 0.35;
  const x0 = (VB_W - S - dx) / 2;
  const y0 = 20 + dy;
  const c = S / base;
  const front = { x: x0, y: y0 };
  const lines: Array<[number, number, number, number]> = [];
  for (let i = 1; i < base; i++) {
    const t = i * c;
    // face avant
    lines.push([front.x + t, front.y, front.x + t, front.y + S]);
    lines.push([front.x, front.y + t, front.x + S, front.y + t]);
    // dessus
    lines.push([front.x + t, front.y, front.x + t + dx, front.y - dy]);
    lines.push([
      front.x + (dx * i) / base,
      front.y - (dy * i) / base,
      front.x + S + (dx * i) / base,
      front.y - (dy * i) / base,
    ]);
    // côté droit
    lines.push([
      front.x + S + (dx * i) / base,
      front.y - (dy * i) / base,
      front.x + S + (dx * i) / base,
      front.y + S - (dy * i) / base,
    ]);
    lines.push([front.x + S, front.y + t, front.x + S + dx, front.y + t - dy]);
  }
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Un cube d’arête ${base} : ${base ** 3} petits cubes.`}
    >
      <polygon
        points={`${front.x},${front.y} ${front.x + dx},${front.y - dy} ${front.x + S + dx},${front.y - dy} ${front.x + S},${front.y}`}
        fill={C1}
        fillOpacity={0.35}
        stroke={INK}
        strokeWidth={1.5}
      />
      <polygon
        points={`${front.x + S},${front.y} ${front.x + S + dx},${front.y - dy} ${front.x + S + dx},${front.y + S - dy} ${front.x + S},${front.y + S}`}
        fill={C1}
        fillOpacity={0.5}
        stroke={INK}
        strokeWidth={1.5}
      />
      <rect
        x={front.x}
        y={front.y}
        width={S}
        height={S}
        fill={C1}
        fillOpacity={0.2}
        stroke={INK}
        strokeWidth={1.5}
      />
      {lines.map(([a, b, cc, d], i) => (
        <line
          key={i}
          x1={a}
          y1={b}
          x2={cc}
          y2={d}
          stroke={INK}
          strokeOpacity={0.45}
          strokeWidth={0.8}
        />
      ))}
      <text x={front.x + S / 2} y={front.y + S + 16} textAnchor="middle" fontSize={13} fill={INK}>
        {base}
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Échelle                                                             */
/* ------------------------------------------------------------------ */

function ScaleMode() {
  const [n, setN] = useState(0);
  const prefix = PREFIXES[n];
  const here = objectsAt(n);
  const decimal = Math.abs(n) <= 12 ? tenPowerDecimal(n) : null;
  return (
    <>
      <ParamSlider
        name="Exposant"
        ariaLabel="Exposant de la puissance de 10, en mètres"
        value={n}
        min={SCALE_MIN}
        max={SCALE_MAX}
        step={1}
        onChange={setN}
        format={(v) => `10${sup(v)} m`}
      />
      <div aria-live="polite" className="space-y-1 text-center">
        <MathText className="text-ink-900 block text-2xl">{`$${powTex(10, n)}\\ \\text{m}$`}</MathText>
        <p className="text-ink-700 text-sm" data-testid="scale-decimal">
          {decimal ? `= ${decimal} m` : `= 1 suivi de ${n} zéros, en mètres`}
          {prefix && ` = 1 ${prefix.symbol} (${prefix.name}mètre)`}
        </p>
      </div>
      <TenRuler n={n} />
      <p className="text-ink-600 text-sm">
        {frenchSpaces(`Un cran vers le haut : ×${NBSP}10. Un cran vers le bas : ÷${NBSP}10.`)}
      </p>
      <ul className="space-y-1" aria-label="De l’atome à la Voie lactée">
        {[...SCALE].reverse().map((o) => {
          const on = o.e === n;
          return (
            <li key={o.name}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => setN(o.e)}
                className={cn(
                  'flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border px-3 text-left text-sm',
                  on
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                    : 'border-ink-100 text-ink-700',
                )}
              >
                <span>{o.name}</span>
                {/* texte simple : pas de KaTeX dans un bouton (nom accessible plus court) */}
                <span className="shrink-0 tabular-nums">{`≈ ${o.m} × 10${sup(o.e)} m`}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-ink-700 text-sm" data-testid="scale-here">
        {frenchSpaces(
          here.length
            ? `Entre ${tenTo(n)} et ${tenTo(n + 1)} m : ${here.map((o) => `${o.name.toLowerCase()} (≈ ${o.m} × ${tenTo(o.e)} m)`).join(', ')}.`
            : `Rien dans la liste entre ${tenTo(n)} et ${tenTo(n + 1)} m : déplace le curseur ou touche un objet.`,
        )}
      </p>
    </>
  );
}

/** un segment de 10ⁿ m partagé en 10 segments de 10ⁿ⁻¹ m : une longueur, pas une aire */
function TenRuler({ n }: { n: number }) {
  const x0 = 20;
  const W = VB_W - 40;
  const y = 44;
  const H = 96;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Un segment de 10 puissance ${n} mètres se partage en 10 segments de 10 puissance ${n - 1} mètres.`}
    >
      <text x={x0 + W / 2} y={y - 18} textAnchor="middle" fontSize={13} fontWeight={700} fill={INK}>
        {`10${sup(n)} m`}
      </text>
      <line x1={x0} x2={x0 + W} y1={y} y2={y} stroke={C1} strokeWidth={4} />
      {Array.from({ length: 11 }, (_, i) => (
        <line
          key={i}
          x1={x0 + (i * W) / 10}
          x2={x0 + (i * W) / 10}
          y1={y - (i % 10 === 0 ? 10 : 6)}
          y2={y + (i % 10 === 0 ? 10 : 6)}
          stroke={INK}
          strokeWidth={1.5}
        />
      ))}
      <line x1={x0} x2={x0 + W / 10} y1={y} y2={y} stroke={C2} strokeWidth={6} />
      <text x={x0} y={y + 30} fontSize={12} fontWeight={700} fill={INK}>
        {`un dixième : 10${sup(n - 1)} m`}
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Écriture scientifique                                               */
/* ------------------------------------------------------------------ */

function SciMode({ onSend, sendCost }: ToolProps) {
  const [text, setText] = useState('0,000 47');
  const [k, setK] = useState(0);
  const sc = scientific(text, k);
  return (
    <>
      <div className="grid grid-cols-2 items-end gap-2">
        <label className="block">
          <span className="text-ink-700 text-sm font-semibold">Nombre</span>
          <input
            type="text"
            inputMode="decimal"
            value={text}
            maxLength={24}
            onChange={(e) => setText(e.target.value)}
            className="border-ink-200 text-ink-900 font-display mt-1 min-h-11 w-full rounded-xl border bg-transparent px-3 text-lg font-bold tabular-nums"
          />
        </label>
        <Stepper
          label="× 10 puissance"
          labelAbove
          value={k}
          min={-12}
          max={12}
          onChange={setK}
          format={fmtExp}
        />
      </div>
      {!sc ? (
        <Note tone="error">
          {frenchSpaces('Écris un nombre décimal, par exemple 3 800 000 ou 0,000 47.')}
        </Note>
      ) : (
        <section
          className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
          aria-live="polite"
        >
          <div data-testid="sci-result">
            <MathText className="text-ink-900 block overflow-x-auto text-center text-2xl">{`$${sc.tex}$`}</MathText>
          </div>
          <p className="text-ink-600 overflow-x-auto text-center text-sm" data-testid="sci-decimal">
            {frenchSpaces(`Écriture décimale : ${sc.decimal}`)}
          </p>
          <StepList steps={scientificSteps(text, k)} testId="sci-steps" />
          {onSend && sc.mantissa !== '0' && (
            <SendToSheetButton
              label={sc.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: sc.atoms })}
            />
          )}
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Règles                                                              */
/* ------------------------------------------------------------------ */

function RulesMode({ rank }: { rank: number }) {
  const [rule, setRule] = useState<Rule>('product');
  const [a, setA] = useState(2);
  const [b, setB] = useState(5);
  const [m, setM] = useState(3);
  const [nExp, setN] = useState(rank >= 4 ? 5 : 2);
  const rules: Array<{ value: Rule; label: string }> = [
    { value: 'product', label: 'Produit' },
    ...(rank >= 4 ? [{ value: 'quotient' as const, label: 'Quotient' }] : []),
    { value: 'same-exponent', label: 'Même exposant' },
  ];
  // 4e : exposants positifs (sauf pour 10)
  // 4e : exposants entiers naturels ; 3e : relatifs
  const lo = rank >= 4 ? -5 : 0;
  const mm = Math.max(rule === 'same-exponent' ? 1 : lo, m);
  const nn = Math.max(lo, nExp);
  const steps = ruleSteps(rule, a, mm, nn, b);
  return (
    <>
      <Segmented ariaLabel="Règle" value={rule} options={rules} onChange={setRule} />
      <div className="grid grid-cols-2 gap-2">
        <Stepper label="Nombre a" labelAbove value={a} min={2} max={10} onChange={setA} />
        {/* dans l'ordre de l'écriture : aᵐ puis aⁿ (ou bⁿ) */}
        <Stepper
          label={rule === 'same-exponent' ? 'Exposant' : 'Exposant m'}
          labelAbove
          value={mm}
          min={rule === 'same-exponent' ? 1 : lo}
          max={9}
          onChange={setM}
          format={fmtExp}
        />
        {rule === 'same-exponent' ? (
          <Stepper label="Nombre b" labelAbove value={b} min={2} max={10} onChange={setB} />
        ) : (
          <Stepper
            label="Exposant n"
            labelAbove
            value={nn}
            min={lo}
            max={9}
            onChange={setN}
            format={fmtExp}
          />
        )}
      </div>
      <FactorChips rule={rule} a={a} b={b} m={mm} n={nn} />
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3" aria-live="polite">
        <StepList steps={steps} testId="rule-steps" />
      </section>
    </>
  );
}

function Chip({ v, color, struck = false }: { v: number; color: string; struck?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-8 min-w-8 items-center justify-center rounded-lg border-2 px-1 text-sm font-bold',
        struck && 'line-through opacity-40',
      )}
      style={{ borderColor: color }}
      aria-hidden
    >
      {v}
    </span>
  );
}

/** les facteurs, quand ils sont peu nombreux : on les voit se regrouper ou se simplifier */
function FactorChips({
  rule,
  a,
  b,
  m,
  n,
}: {
  rule: Rule;
  a: number;
  b: number;
  m: number;
  n: number;
}) {
  if (rule === 'same-exponent') {
    if (m > 6) return null;
    return (
      <div className="flex flex-wrap items-center gap-1" data-testid="chips">
        {Array.from({ length: m }, (_, i) => (
          <span
            key={i}
            className="border-ink-200 inline-flex items-center gap-0.5 rounded-xl border px-1 py-0.5"
          >
            <Chip v={a} color={C1} />
            <span className="text-ink-500 text-xs" aria-hidden>
              ×
            </span>
            <Chip v={b} color={C2} />
          </span>
        ))}
      </div>
    );
  }
  if (m < 0 || n < 0 || m + n > 12) return null;
  if (rule === 'product')
    return (
      <div className="flex flex-wrap items-center gap-1" data-testid="chips">
        {Array.from({ length: m }, (_, i) => (
          <Chip key={`m${i}`} v={a} color={C1} />
        ))}
        {Array.from({ length: n }, (_, i) => (
          <Chip key={`n${i}`} v={a} color={C2} />
        ))}
      </div>
    );
  const c = Math.min(m, n);
  return (
    <div className="inline-flex flex-col items-start gap-1" data-testid="chips">
      <div className="flex flex-wrap gap-1">
        {m === 0 ? (
          <span className="text-ink-700 px-2 text-sm">1</span>
        ) : (
          Array.from({ length: m }, (_, i) => <Chip key={i} v={a} color={C1} struck={i < c} />)
        )}
      </div>
      <div className="bg-ink-700 h-0.5 w-full" aria-hidden />
      <div className="flex flex-wrap gap-1">
        {n === 0 ? (
          <span className="text-ink-700 px-2 text-sm">1</span>
        ) : (
          Array.from({ length: n }, (_, i) => <Chip key={i} v={a} color={C2} struck={i < c} />)
        )}
      </div>
    </div>
  );
}
